import { FormInstance, Rule } from 'antd/es/form';
import { Form } from 'antd/lib';
import React, { useEffect, useMemo } from 'react';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import CommonInput from './CommonInput.component';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    style?: React.CSSProperties;
    name?: string;
    placeholder?: string;
    suffix?: any;
    disabled?: any;
    maxLength?: number;
    required?: boolean;
    showRequiredError?: boolean;
    fraction?: boolean;
    callback?: any;
    max?: number | string;
    min?: number | string;
    subtract?: boolean;
    rules?: Rule[];
    label?: string;
    type?: string;
    labelClassName?: string;
    inputClassName?: string;
    onChange?: (value: any) => void;
    onValidateError?: (isError?: boolean) => void;
    size?: any;
    formatNumber?: boolean;
    onlyRomanji?: boolean;
    currentForm?: FormInstance<any>;
    customFormat?: string;
    thousandSuffix?: string;
    maximumFractionDigits?: number;
    fomatDouble?: boolean;
}

export const FormInput = ({
    suffix,
    prefix,
    disabled,
    style,
    maxLength,
    rules = [],
    label,
    required,
    type = 'text',
    size = 'large',
    min,
    max,
    labelClassName = '',
    name,
    formatNumber = true,
    thousandSuffix,
    inputClassName = '',
    showRequiredError = true,
    fomatDouble = false,
    ...props
}: InputProps) => {
    const [itemValue, setItemValue] = React.useState<string>('');
    const [isRequire, setIsRequire] = React.useState<boolean>(required ?? false);
    const [validateRules, setValidateRules] = React.useState<Rule[]>([]);
    const [customFormat, setCustomFormat] = React.useState<string>(props.customFormat ?? '');
    const [suffixDisplay, setSuffixDisplay] = React.useState<any>('');
    useEffect(() => {
        if (!itemValue && suffix && thousandSuffix) {
            setSuffixDisplay(
                <span>
                    <span style={{ color: '#8D9AB4' }}>{thousandSuffix}</span>
                    {suffix}
                </span>,
            );
        } else {
            setSuffixDisplay(suffix);
        }
    }, [itemValue]);

    const addValidateNumber = (rules: Rule[]) => {
        const minValue = Number(min);
        let maxValue = Number(max);
        if (!max && maxLength) {
            maxValue = Math.pow(10, maxLength) - 1;
        }
        if (minValue && rules && rules?.findIndex((rule: any) => rule.id === 'min') === -1) {
            rules?.push(() => ({
                id: 'min',
                validator(_: any, val: any) {
                    let validateValue = val;
                    if (validateValue && isNaN(validateValue)) {
                        validateValue = Number((validateValue + '').replaceAll(',', ''));
                    }
                    if ((validateValue || validateValue === 0) && !isNaN(validateValue)) {
                        if (Number(validateValue) < minValue) {
                            if (
                                fomatDouble &&
                                String(validateValue).charAt(String(validateValue).length - 2) !=
                                    '.'
                            ) {
                                return Promise.reject('専有面積はxxxx.xで入力してください。');
                            }
                            return Promise.reject(
                                getMessage(
                                    'In_ValRng_FromTo',
                                    label,
                                    min ?? 0,
                                    max ? max : commonHelpers.formatNumberMoney(maxValue),
                                ),
                            );
                        }
                    }
                    return Promise.resolve();
                },
            }));
        }
        if (maxValue && rules && rules?.findIndex((rule: any) => rule.id === 'max') === -1) {
            rules?.push(() => ({
                id: 'max',
                validator(_: any, val: any) {
                    let validateValue = val;
                    if (validateValue && isNaN(validateValue)) {
                        validateValue = Number((validateValue + '').replaceAll(',', ''));
                    }
                    if ((validateValue || validateValue === 0) && !isNaN(validateValue)) {
                        if (Number(validateValue) > maxValue) {
                            if (
                                fomatDouble &&
                                String(validateValue).charAt(String(validateValue).length - 2) !=
                                    '.'
                            ) {
                                return Promise.reject('専有面積はxxxx.xで入力してください。');
                            }
                            return Promise.reject(
                                getMessage(
                                    'In_ValRng_FromTo',
                                    label,
                                    min ?? 0,
                                    max ? max : commonHelpers.formatNumberMoney(maxValue),
                                ),
                            );
                        }
                    }
                    return Promise.resolve();
                },
            }));
        }
    };

    const checkRuleRequire = (rules: Rule[]) => {
        let isRequire = false;
        rules.forEach((rule: any) => {
            if (rule.required) {
                isRequire = true;
            }
        });
        return isRequire;
    };

    useEffect(() => {
        const tempRule = rules;
        const ruleRequire = checkRuleRequire(rules);
        if (ruleRequire) {
            setIsRequire(true);
        } else if (required) {
            tempRule.push(() => ({
                required: true,
                message: showRequiredError ? getMessage('In_Required', label) : '',
            }));
            setIsRequire(true);
        } else {
            setIsRequire(false);
        }
        if (type === 'number') {
            addValidateNumber(tempRule);
        }
        if (type === 'email' && tempRule.findIndex((rule: any) => rule.type === 'email') === -1) {
            tempRule.push(() => ({
                type: type,
                message: getMessage('In_Fmt_Mail', label),
            }));
        }
        setValidateRules(tempRule);
        if(type == 'text' && maxLengthCalc){
            tempRule.push(
                () => ({
                    max: maxLengthCalc,
                    message: getMessage('In_MaxLength', label, maxLengthCalc),
                })
            )
        }
    }, [required]);

    const maxLengthCalc = useMemo(() => {
        // if (maxLength && type === 'phone') {
        //     return maxLength + 2;
        // }
        if (maxLength && type === 'number') {
            if (formatNumber) {
                return commonHelpers.getNumberFormatMaxLength(maxLength, props.fraction ? 1 : 0);
            }
            return maxLength;
        }
        // if (maxLength && type === 'postcode') {
        //     return maxLength + 1;
        // }
        return maxLength;
    }, [maxLength, itemValue]);

    return (
        <>
            <span className={labelClassName}>
                {label}
                {label && isRequire && <span style={{ color: '#EB5757' }}>*</span>}
            </span>
            <Form.Item name={name} rules={validateRules} className={props.className}>
                <CommonInput
                    {...props}
                    {...{ style, value: itemValue }}
                    rules={validateRules}
                    name={name}
                    type={type}
                    size={size}
                    className={inputClassName}
                    suffix={suffixDisplay}
                    prefix={prefix}
                    disabled={disabled}
                    maxLength={maxLengthCalc}
                    formatNumber={formatNumber}
                    customFormat={customFormat}
                    onChange={(value) => {
                        setItemValue(value);
                        props.onChange && props.onChange(value);
                    }}
                />
            </Form.Item>
        </>
    );
};
